import { clampRgb, modeLrgb, modeOklch, modeRgb, parse, useMode as registerMode, wcagContrast } from 'culori/fn';

registerMode(modeOklch);
registerMode(modeRgb);
registerMode(modeLrgb);

const WHITE_INK = '#ffffff';
const BLACK_INK = '#000000';

export type Ink = typeof WHITE_INK | typeof BLACK_INK;

/** Below this against the canvas, a colour reads as missing rather than as a colour. */
const VISIBLE_CONTRAST = 1.6;

/** Clipped first, because that is what the browser draws. */
export const higherContrastInk = (color: string): Ink => {
  const drawn = clampRgb(color);
  if (!drawn) {
    return BLACK_INK;
  }

  return wcagContrast(WHITE_INK, drawn) >= wcagContrast(BLACK_INK, drawn) ? WHITE_INK : BLACK_INK;
};

/**
 * Returns a colour that stays visible against the canvas,
 * falling back when the colour is missing, unparseable, or too close to the canvas to be seen.
 */
export const getContrastColor = (
  color: string | undefined,
  canvas: string = WHITE_INK,
  fallback = 'var(--muted-foreground)',
): string => {
  const parsed = color ? parse(color) : undefined;
  if (!color || !parsed) {
    return fallback;
  }

  return wcagContrast(parsed, canvas) < VISIBLE_CONTRAST ? fallback : color;
};

/** Returns a text colour for the given fill, preferring the browser's own `contrast-color()`. */
export const getContrastTextColor = (color: string): string => {
  if (globalThis.CSS?.supports?.('color', `contrast-color(${color})`)) {
    return `contrast-color(${color})`;
  }

  return higherContrastInk(color);
};
